import { Grid, Tooltip } from '@mui/material'
import { ReactNode, useMemo } from 'react'
import { Control, FieldPath, FieldValues, useWatch } from 'react-hook-form'
import { FieldPrerequisiteSpecShape, useFieldPrerequisites } from './BaseWorksheetForm.helpers'

// The components in this file contain NO business logic. They exist purely as
// rerender-optimization wrappers around worksheet fields: gating mounting on
// prerequisite readiness so expensive hooks/effects don't fire until needed,
// and isolating `useWatch` subscriptions so field-value changes only re-render
// the leaf subtree (not the parent form).

const DEFAULT_GRID_SIZE = { xs: 12, md: 4 } as const

export type FieldReadyGateValues<
  T extends FieldValues,
  TRequired extends readonly FieldPrerequisiteSpecShape<T>[],
  TWatch extends readonly (keyof T & string)[],
> = {
  [K in TRequired[number]['name'] | TWatch[number]]: K extends keyof T ? T[K] : never
}

export interface FieldReadyGateProps<
  T extends FieldValues,
  TRequired extends readonly FieldPrerequisiteSpecShape<T>[],
  TWatch extends readonly (keyof T & string)[] = readonly [],
> {
  control: Control<T>
  requiredFields: TRequired
  // Non-blocking fields to watch and forward to `children` (e.g. the field itself, optional flags).
  watch?: TWatch
  // Loading-registry id this gate broadcasts under for chain propagation.
  selfName: string
  shell: ReactNode
  children: (values: FieldReadyGateValues<T, TRequired, TWatch>) => ReactNode
  gridSize?: typeof DEFAULT_GRID_SIZE | { xs: number; md?: number; lg?: number; xl?: number }
}

// Rerender-optimization wrapper. While the required fields aren't ready, only
// `shell` is mounted — the active subtree's hooks/effects don't fire until needed.
// No business logic lives here; readiness is delegated to `useFieldPrerequisites`.
export const FieldReadyGate = <
  T extends FieldValues,
  const TRequired extends readonly FieldPrerequisiteSpecShape<T>[],
  const TWatch extends readonly (keyof T & string)[] = readonly [],
>({
  control,
  requiredFields,
  watch,
  selfName,
  shell,
  children,
  gridSize = DEFAULT_GRID_SIZE,
}: FieldReadyGateProps<T, TRequired, TWatch>) => {
  const prereq = useFieldPrerequisites(control, requiredFields, selfName)

  return (
    <Grid size={gridSize}>
      <Tooltip title={prereq.tooltip} disableHoverListener={prereq.isReady} placement="top">
        <span>
          {prereq.isReady ? (
            <FieldReadyGateActive control={control} requiredFields={requiredFields} watch={watch}>
              {children}
            </FieldReadyGateActive>
          ) : (
            shell
          )}
        </span>
      </Tooltip>
    </Grid>
  )
}

interface FieldReadyGateActiveProps<
  T extends FieldValues,
  TRequired extends readonly FieldPrerequisiteSpecShape<T>[],
  TWatch extends readonly (keyof T & string)[],
> {
  control: Control<T>
  requiredFields: TRequired
  watch?: TWatch
  children: (values: FieldReadyGateValues<T, TRequired, TWatch>) => ReactNode
}

// Rerender-optimization inner: isolates the `useWatch` subscription so that
// field-value changes only re-render this leaf, not the enclosing form.
// No business logic — it just forwards the watched values to `children`.
const FieldReadyGateActive = <
  T extends FieldValues,
  TRequired extends readonly FieldPrerequisiteSpecShape<T>[],
  TWatch extends readonly (keyof T & string)[],
>({
  control,
  requiredFields,
  watch,
  children,
}: FieldReadyGateActiveProps<T, TRequired, TWatch>) => {
  const names = useMemo(
    () => [...requiredFields.map((p) => p.name), ...(watch ?? [])] as FieldPath<T>[],
    [requiredFields, watch],
  )
  const watched = useWatch({ control, name: names }) as unknown as unknown[]

  const values = useMemo(() => {
    const out: Record<string, unknown> = {}
    names.forEach((n, i) => {
      out[n as string] = watched[i]
    })
    return out as FieldReadyGateValues<T, TRequired, TWatch>
  }, [names, watched])

  return <>{children(values)}</>
}
